/**
 * Was ein Vorgang kostet — im Paket und im Abo — und welcher Weg für eine
 * Nutzung am günstigsten ist (Konzept „Kostenlos anmelden, mit Credits
 * bezahlen“, 02.11.2026).
 *
 * Rein, ohne Abruf: Die Preisseite (Karten, FAQ), der Rechner unter „Tarif &
 * Credits“ und die Karte bei fehlenden Credits rechnen damit, und `node --test`
 * prüft es (`creditPreise.test.ts`). Jede Zahl kommt aus dem Katalog — Preise,
 * Credits im Monat, Paketleiter, Kosten je Vorgang; hier steht nur die Rechnung.
 *
 * Alle Beträge in Minor Units (Cent) der gewählten Währung. Ein Preis, den der
 * Katalog in dieser Währung nicht führt, ist `null` und wird nicht umgerechnet
 * (Regel #7) — dieselbe Regel wie `tarifkarten`.
 */
import type { Katalog } from '../hooks/useKatalog'
import type { Tarifkarte } from './katalogMarkt'

export type Intervall = 'monthly' | 'yearly'

export interface PaketPreis {
  key: string
  credits: number
  cents: number
}

/** Die Pakete mit einem Preis in `waehrung`, kleinstes zuerst. */
export function paketPreise(katalog: Katalog | undefined, waehrung: string): PaketPreis[] {
  return (katalog?.sim_credit_packs ?? [])
    .flatMap((p) => {
      const cents = p.prices.find((x) => x.currency === waehrung)?.unit_amount_cents
      return typeof cents === 'number' && p.size > 0 ? [{ key: p.key, credits: p.size, cents }] : []
    })
    .sort((a, b) => a.credits - b.credits)
}

/** Das kleinste Paket, das `credits` deckt — reicht keines, das größte; ohne Pakete `null`. */
export function kleinstesPassendesPaket(pakete: PaketPreis[], credits: number): PaketPreis | null {
  if (pakete.length === 0) return null
  return pakete.find((p) => p.credits >= credits) ?? pakete[pakete.length - 1]
}

/** Der günstigste Credit der Leiter, in Cent je Credit (meist das größte Paket). */
function bestSatz(pakete: PaketPreis[]): number | null {
  if (pakete.length === 0) return null
  return Math.min(...pakete.map((p) => p.cents / p.credits))
}

function ggt(a: number, b: number): number {
  return b === 0 ? a : ggt(b, a % b)
}

/** Ab so vielen Einheiten rechnet `paketKosten` zum besten Satz statt genau (Schutz vor riesigen Tabellen). */
const GENAU_BIS_EINHEITEN = 200_000

/**
 * Die günstigste Kombination von Paketen, die mindestens `credits` deckt, in
 * Cent. Pakete verfallen nicht — wer übers Jahr 60.000 Credits braucht, kauft
 * zwei 30.000er, nicht ein 130.000er. `0` für nichts, `null` ohne Pakete.
 */
export function paketKosten(credits: number, pakete: PaketPreis[]): number | null {
  if (credits <= 0) return 0
  if (pakete.length === 0) return null
  const einheit = pakete.reduce((g, p) => ggt(g, p.credits), 0)
  const n = Math.ceil(credits / einheit)
  if (n > GENAU_BIS_EINHEITEN) {
    return Math.ceil(credits * (bestSatz(pakete) as number))
  }
  const groessen = pakete.map((p) => p.credits / einheit)
  // kosten[k] = günstigste Kombination für mindestens k Einheiten
  const kosten = new Float64Array(n + 1)
  for (let k = 1; k <= n; k++) {
    let best = Infinity
    for (let i = 0; i < pakete.length; i++) {
      const rest = k - groessen[i]
      const c = pakete[i].cents + (rest > 0 ? kosten[rest] : 0)
      if (c < best) best = c
    }
    kosten[k] = best
  }
  return kosten[n]
}

/** Der Monatsbetrag einer Stufe in Cent: monatlich der Monatspreis, jährlich ein Zwölftel des Jahrespreises. */
export function monatsPreisCents(stufe: Tarifkarte, intervall: Intervall): number | null {
  const preis = intervall === 'yearly' ? stufe.price_yearly : stufe.price_monthly
  if (preis === null || preis === undefined) return null
  return intervall === 'yearly' ? (preis * 100) / 12 : preis * 100
}

/** Die Credits im Monat einer Stufe — `0`, wenn sie keine trägt oder unbegrenzt ist. */
function monatsCredits(stufe: Tarifkarte): number {
  const wert = stufe.features?.credits_per_month
  return typeof wert === 'number' && wert > 0 ? wert : 0
}

/** Was ein Vorgang in der Stufe kostet, in Cent (ungerundet) — `null` ohne Preis oder ohne Credits im Monat. */
export function vorgangsPreisCents(stufe: Tarifkarte, intervall: Intervall, kostenJeVorgang: number): number | null {
  const monat = monatsPreisCents(stufe, intervall)
  const credits = monatsCredits(stufe)
  if (monat === null || credits === 0 || kostenJeVorgang <= 0) return null
  return monat / (credits / kostenJeVorgang)
}

/** Was ein Vorgang im kleinsten und im günstigsten Paket kostet, in Cent. */
export function paketVorgangsPreis(
  pakete: PaketPreis[],
  kostenJeVorgang: number,
): { hoechstens: number; mindestens: number } | null {
  const satz = bestSatz(pakete)
  if (satz === null || kostenJeVorgang <= 0) return null
  const kleinstes = pakete[0]
  return { hoechstens: (kleinstes.cents / kleinstes.credits) * kostenJeVorgang, mindestens: satz * kostenJeVorgang }
}

/**
 * Ab wie vielen Vorgängen im Monat die Stufe nicht mehr kostet als die
 * Alternative: Pakete allein (`unten` = `null`) oder die Stufe darunter mit
 * Paketen für den Rest. Gerechnet zum Satz des günstigsten Pakets — der
 * Vergleich, bei dem Pakete am besten abschneiden. `null`, wenn die Stufe
 * innerhalb ihres eigenen Monats nie günstiger wird.
 */
export function lohntSichAb(
  stufe: Tarifkarte,
  unten: Tarifkarte | null,
  intervall: Intervall,
  pakete: PaketPreis[],
  kostenJeVorgang: number,
): number | null {
  const preis = monatsPreisCents(stufe, intervall)
  const satz = bestSatz(pakete)
  if (preis === null || satz === null || kostenJeVorgang <= 0) return null
  const basis = unten ? monatsPreisCents(unten, intervall) : 0
  if (basis === null) return null
  const drin = unten ? monatsCredits(unten) / kostenJeVorgang : 0
  const eigene = Math.floor(monatsCredits(stufe) / kostenJeVorgang)
  for (let n = 1; n <= eigene; n++) {
    if (preis <= basis + Math.max(0, n - drin) * kostenJeVorgang * satz) return n
  }
  return null
}

export interface Weg {
  /** `null` = nur Pakete, sonst die Kennung der Stufe (mit Paketen für den Rest). */
  code: string | null
  /** Was der Weg im Jahr kostet, in Cent. */
  centsImJahr: number
  /** Davon für Pakete — bei einer Stufe der Rest über ihren Monat hinaus. */
  paketCentsImJahr: number
}

/**
 * Die Wege für einen Bedarf an Credits im Monat, günstigster zuerst: nur
 * Pakete, oder eine Stufe für die eigene Anlage mit Paketen für den Rest.
 *
 * Gerechnet über ein Jahr, weil gekaufte Credits nicht verfallen, Credits
 * aus dem Abo aber am Monatsende: Pakete für zwölf Monate Bedarf (bzw.
 * Überhang), die Stufe mit zwölf Monatspreisen oder dem Jahrespreis.
 * Stufen für Kunden und Teams (`scope = organization`) und solche ohne Preis
 * in der Währung zählen nicht mit; ohne Pakete gibt es den Paket-Weg nicht.
 */
export function guenstigsteWege(
  bedarfImMonat: number,
  stufen: Tarifkarte[],
  pakete: PaketPreis[],
  intervall: Intervall,
): Weg[] {
  const wege: Weg[] = []
  const nurPakete = paketKosten(bedarfImMonat * 12, pakete)
  if (nurPakete !== null) wege.push({ code: null, centsImJahr: nurPakete, paketCentsImJahr: nurPakete })
  for (const stufe of stufen) {
    if (stufe.scope !== 'user') continue
    const monat = monatsPreisCents(stufe, intervall)
    if (monat === null || monatsCredits(stufe) === 0) continue
    const ueberhang = Math.max(0, bedarfImMonat - monatsCredits(stufe)) * 12
    const fuerPakete = paketKosten(ueberhang, pakete)
    if (fuerPakete === null) continue
    wege.push({ code: stufe.code, centsImJahr: Math.round(monat * 12) + fuerPakete, paketCentsImJahr: fuerPakete })
  }
  return wege.sort((a, b) => a.centsImJahr - b.centsImJahr)
}
